/**
 * Vendor › Products we buy › "New input product" (TASK-0259, FRESH-1): an input product created from a
 * vendor code, one per purchased form ("Stonehill 101 chuck, grain-fed"). The name is prefilled
 * "<Vendor> <code> <description>" and stays editable; unit lb / ea; a family (required); "usually becomes"
 * (optional, the 6-month picker). `POST /products { purchasingOnly: true, ... }`, then the vendor link with
 * the code (`createProductAndLink`), then `PUT /products/:id/becomes` when chosen.
 */
import { useId, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, errorMessage } from '@/lib/api/client';
import type { DepartmentListResponse, ProductSummary } from '@/lib/api/types';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Consequences } from '@/ui/Callout';
import { Dialog } from '@/ui/Dialog';
import { Field, Input, Select } from '@/ui/Field';
import { activeFamilies, useFamilies } from '@/features/catalog/families';
import { LinkPicker } from '@/features/catalog/InputLinksPanel';
import { createProductAndLink, vendorKeys } from './api';

export const INPUT_CONSEQUENCE =
  'Never sold: the registers will not see it. It is bought, received and invoiced like any product.';

/** "Stonehill 101 chuck, grain-fed": the vendor's short name, the code, the description. */
export function inputName(vendor: string, code: string, description: string): string {
  return [
    vendor.trim().split(/\s+/).slice(0, 2).join(' '),
    code.trim(),
    description.trim(),
  ]
    .filter(Boolean)
    .join(' ');
}

export function NewInputProductDialog(props: {
  open: boolean;
  vendorId: number;
  vendorName: string;
  onOpenChange: (open: boolean) => void;
}) {
  // Mounted only while open: a vendor page must not fetch families and departments for a closed dialog.
  if (!props.open) return null;
  return <NewInputProductForm {...props} />;
}

function NewInputProductForm({
  open,
  vendorId,
  vendorName,
  onOpenChange,
}: {
  open: boolean;
  vendorId: number;
  vendorName: string;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const families = useFamilies();
  const ids = useId();
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [name, setName] = useState('');
  const [nameEdited, setNameEdited] = useState(false);
  const [unit, setUnit] = useState<'lb' | 'ea'>('lb');
  const [family, setFamily] = useState('');
  const [departmentId, setDepartmentId] = useState<number | null>(null);
  const [becomes, setBecomes] = useState<ProductSummary[]>([]);

  const departments = useQuery({
    queryKey: ['departments', { pageSize: 500 }],
    queryFn: () =>
      api.get<DepartmentListResponse>('/departments', { params: { pageSize: 500 } }),
    staleTime: 5 * 60_000,
  });

  const suggested = useMemo(
    () => inputName(vendorName, code, description),
    [vendorName, code, description]
  );
  const shownName = nameEdited ? name : suggested;

  const create = useMutation({
    mutationFn: async () => {
      const { product, linkWarning } = await createProductAndLink(vendorId, {
        name: shownName.trim(),
        price: 0,
        purchasingOnly: true,
        productionFamily: family,
        departmentId,
        cost: null,
        barcode: null,
        vendorSku: code.trim() || null,
        unitsPerCase: null,
        caseCost: null,
        unit,
      });
      let becomesWarning: string | null = null;
      if (becomes.length > 0) {
        try {
          await api.put(`/products/${product.id}/becomes`, {
            productIds: becomes.map((b) => Number(b.id)),
          });
        } catch (error) {
          becomesWarning = `Created, but "usually becomes" was not saved: ${errorMessage(error)}. Set it on the product page.`;
        }
      }
      return { product, linkWarning, becomesWarning };
    },
    onSuccess: ({ product, linkWarning, becomesWarning }) => {
      void qc.invalidateQueries({ queryKey: vendorKeys.all });
      void qc.invalidateQueries({ queryKey: ['products'] });
      onOpenChange(false);
      if (linkWarning) toast.warning(`Created "${product.name}". ${linkWarning}`);
      else if (becomesWarning)
        toast.warning(`Created "${product.name}". ${becomesWarning}`);
      else
        toast.success(
          `Created the input "${product.name}" with code ${code.trim() || '—'} on this vendor.`
        );
    },
    onError: (error) =>
      toast.error('Could not create the input product', {
        description: errorMessage(error),
      }),
  });

  const canCreate = shownName.trim() !== '' && family !== '' && code.trim() !== '';

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="New input product"
      description={`A purchased form from ${vendorName}, bought for the butcher or kitchen and never sold as is. One product per vendor code.`}
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={!canCreate}
            onClick={() => create.mutate()}
          >
            Create input
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field
          label="Vendor code"
          htmlFor={`${ids}-code`}
          required
          hint="Goes on the vendor link; invoices match on it."
        >
          <Input
            id={`${ids}-code`}
            size="sm"
            mono
            autoFocus
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
          />
        </Field>
        <Field
          label="Description"
          htmlFor={`${ids}-description`}
          hint="The cut and grade, as the vendor names it."
        >
          <Input
            id={`${ids}-description`}
            size="sm"
            value={description}
            placeholder="chuck, grain-fed"
            onChange={(e) => setDescription(e.currentTarget.value)}
          />
        </Field>
        <Field
          label="Name"
          htmlFor={`${ids}-name`}
          required
          className="col-span-2"
          hint="Prefilled from the vendor, code and description; edit it freely."
        >
          <Input
            id={`${ids}-name`}
            size="sm"
            value={shownName}
            onChange={(e) => {
              setNameEdited(true);
              setName(e.currentTarget.value);
            }}
          />
        </Field>
        <Field label="Unit" htmlFor={`${ids}-unit`} required>
          <Select
            id={`${ids}-unit`}
            size="sm"
            value={unit}
            onChange={(e) => setUnit(e.currentTarget.value === 'ea' ? 'ea' : 'lb')}
          >
            <option value="lb">lb (by weight)</option>
            <option value="ea">ea (each)</option>
          </Select>
        </Field>
        <Field
          label="Family"
          htmlFor={`${ids}-family`}
          required
          hint="Chicken, beef… the family of what it becomes."
        >
          <Select
            id={`${ids}-family`}
            size="sm"
            value={family}
            onChange={(e) => setFamily(e.currentTarget.value)}
          >
            <option value="">—</option>
            {activeFamilies(families.data).map((f) => (
              <option key={f.code} value={f.code}>
                {f.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Department"
          htmlFor={`${ids}-department`}
          hint="Only for the in / out report's department view; the registers never see an input."
          className="col-span-2"
        >
          <Select
            id={`${ids}-department`}
            size="sm"
            value={departmentId ?? ''}
            onChange={(e) =>
              setDepartmentId(
                e.currentTarget.value ? Number(e.currentTarget.value) : null
              )
            }
          >
            <option value="">—</option>
            {(departments.data?.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Usually becomes"
          className="col-span-2"
          hint="Optional. The sold product this input turns into; a hint for finding things, nothing else."
        >
          {becomes.length > 0 ? (
            <div className="mb-1.5 flex flex-wrap gap-1.5">
              {becomes.map((b) => (
                <Badge key={b.id}>
                  {b.name}
                  <button
                    type="button"
                    className="ml-1"
                    aria-label={`Remove ${b.name}`}
                    onClick={() =>
                      setBecomes((prev) => prev.filter((x) => x.id !== b.id))
                    }
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
          ) : null}
          <LinkPicker
            side="becomes"
            family={family || null}
            excludeIds={new Set(becomes.map((b) => Number(b.id)))}
            onPick={(row) => setBecomes((prev) => [...prev, row])}
          />
        </Field>
      </div>
      <Consequences
        tone="info"
        className="mt-3"
        items={[
          INPUT_CONSEQUENCE,
          'Its stock is never counted; the in / out report reads what arrived.',
        ]}
      />
    </Dialog>
  );
}
